/**
 * FEEDBACK — the two gestures a form makes: refusing and confirming.
 *
 * `Shake`  wraps a field; flips `active` to play the refusal once. Re-arming
 *          on the next attempt replays it (the key changes with `attempt`).
 * `Pop`    a tick or badge landing with one small overshoot.
 * `InlineMessage` a validation line unfolding under its field, with the space
 *          animated open so the button below slides rather than jumps.
 */
import React, { useEffect } from 'react';
import { AnimatePresence, motion, useAnimationControls } from 'framer-motion';
import { inlineMessage, pop, shake } from './presets';

interface ShakeProps {
  active: boolean;
  /** Increment on every failed attempt so a second refusal replays. */
  attempt?: number;
  children: React.ReactNode;
  className?: string;
}

/**
 * Replays IN PLACE. The first cut keyed the wrapper on `attempt`, which
 * remounted the field inside it on every refusal: the input lost focus, and
 * anything holding a reference to it (a test, a ref) was left pointing at a
 * detached node. Animation controls restart the keyframes on the same element
 * instead, so the child is never touched.
 */
export const Shake: React.FC<ShakeProps> = ({
  active,
  attempt = 0,
  children,
  className,
}) => {
  const controls = useAnimationControls();

  useEffect(() => {
    void controls.start(active ? 'shake' : 'still');
  }, [active, attempt, controls]);

  return (
    <motion.div
      className={className}
      variants={shake}
      initial="still"
      animate={controls}
    >
      {children}
    </motion.div>
  );
};

interface PopProps {
  children: React.ReactNode;
  className?: string;
  as?: 'span' | 'div';
}

export const Pop: React.FC<PopProps> = ({ children, className, as = 'span' }) => {
  const Tag = as === 'div' ? motion.div : motion.span;

  return (
    <Tag
      className={className}
      variants={pop}
      initial="hidden"
      animate="visible"
      style={{ display: as === 'div' ? 'block' : 'inline-flex' }}
    >
      {children}
    </Tag>
  );
};

interface InlineMessageProps {
  /** The message to show; nothing renders (and the space closes) when empty. */
  message?: React.ReactNode;
  className?: string;
  id?: string;
  role?: 'alert' | 'status';
}

export const InlineMessage: React.FC<InlineMessageProps> = ({
  message,
  className,
  id,
  role = 'alert',
}) => (
  <AnimatePresence initial={false}>
    {message ? (
      <motion.div
        key="message"
        variants={inlineMessage}
        initial="hidden"
        animate="visible"
        exit="hidden"
        style={{ overflow: 'hidden' }}
      >
        <div id={id} role={role} className={className}>
          {message}
        </div>
      </motion.div>
    ) : null}
  </AnimatePresence>
);
